import React, { useState, useCallback, useEffect } from 'react';
import { Chess } from 'chess.js';
import toast from 'react-hot-toast';
import { db } from '../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';

// 🔥 IMPORT PRO SOUND ENGINE & THEMES
import { playSound, PIECE_SETS, BOARD_THEMES } from '../components/Board/BoardConstants';

export default function Analysis({ user }) {
  const [boardTheme, setBoardTheme] = useState('green');
  const [pieceTheme, setPieceTheme] = useState('cburnett');

  useEffect(() => {
    if (!user?.email) return;
    const unsub = onSnapshot(doc(db, 'users', user.email.toLowerCase()), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        if (d.boardTheme) setBoardTheme(d.boardTheme);
        if (d.pieceTheme) setPieceTheme(d.pieceTheme);
      }
    });
    return () => unsub();
  }, [user]);

  const { light: lightSquareColor, dark: darkSquareColor } = BOARD_THEMES[boardTheme] || BOARD_THEMES.green;
  const currentPieceSet = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;
  const [game, setGame] = useState(new Chess());
  const [fen, setFen] = useState(game.fen());
  const [fenInput, setFenInput] = useState('');
  const [pgnInput, setPgnInput] = useState('');
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [isFlipped, setIsFlipped] = useState(false);
  
  const [history, setHistory] = useState([new Chess().fen()]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);

  const updateGameSafe = (newFen) => {
    try {
      const temp = new Chess(newFen);
      setGame(temp); setFen(temp.fen());
    } catch (e) {}
  };

  const loadFen = (newFen) => {
    try {
      const tempGame = new Chess(newFen);
      setGame(tempGame); setFen(tempGame.fen());
      setSelectedSquare(null); setLegalMoves([]);
      setHistory([tempGame.fen()]); setCurrentMoveIndex(0);
      toast.success("Position Loaded!");
    } catch (e) { toast.error("Invalid FEN!"); }
  };

  const loadPgn = (newPgn) => {
    try {
      const tempGame = new Chess();
      tempGame.loadPgn(newPgn);
      
      const moves = tempGame.history();
      const replayGame = new Chess();
      const newHistory = [replayGame.fen()];
      
      for (let move of moves) {
        replayGame.move(move);
        newHistory.push(replayGame.fen());
      }
      
      setHistory(newHistory); setCurrentMoveIndex(newHistory.length - 1);
      updateGameSafe(newHistory[newHistory.length - 1]);
      setSelectedSquare(null); setLegalMoves([]);
      toast.success("Game Loaded!");
    } catch (e) { toast.error("Invalid PGN format!"); }
  };

  // 🔥 NEW ATTEMPT MOVE (Handles both click and drag)
  const attemptMove = (from, to) => {
      try {
          const move = game.move({ from, to, promotion: 'q' });
          if (move) {
              playSound(move.captured ? 'capture' : 'move');
              const newFen = game.fen();
              const newHistory = history.slice(0, currentMoveIndex + 1);
              newHistory.push(newFen);
              setHistory(newHistory);
              setCurrentMoveIndex(newHistory.length - 1);
              setFen(newFen);
          }
      } catch (e) { playSound('error'); }
      setSelectedSquare(null); setLegalMoves([]);
  };

  const handleSquareClick = (sq) => {
    if (selectedSquare) {
      if (legalMoves.includes(sq)) attemptMove(selectedSquare, sq);
      else {
          const piece = game.get(sq);
          if (piece && piece.color === game.turn()) {
              setSelectedSquare(sq);
              setLegalMoves(game.moves({ square: sq, verbose: true }).map(m => m.to));
          } else { setSelectedSquare(null); setLegalMoves([]); }
      }
    } else {
      const piece = game.get(sq);
      if (piece) {
        setSelectedSquare(sq);
        setLegalMoves(game.moves({ square: sq, verbose: true }).map(m => m.to));
      }
    }
  };

  const resetBoard = () => {
    const tempGame = new Chess(); setGame(tempGame);
    const startFen = tempGame.fen(); setFen(startFen);
    setSelectedSquare(null); setLegalMoves([]);
    setHistory([startFen]); setCurrentMoveIndex(0);
    toast("Board Reset", { icon: '🧹' });
  };

  const stepBackward = useCallback(() => {
    if (currentMoveIndex > 0) {
      const prevIndex = currentMoveIndex - 1;
      setCurrentMoveIndex(prevIndex); updateGameSafe(history[prevIndex]);
      setSelectedSquare(null); setLegalMoves([]);
      playSound('move'); 
    }
  }, [currentMoveIndex, history]);

  const stepForward = useCallback(() => {
    if (currentMoveIndex < history.length - 1) {
      const nextIndex = currentMoveIndex + 1;
      const piecesPrev = history[currentMoveIndex].split(' ')[0].replace(/[^a-zA-Z]/g, '').length;
      const piecesNow = history[nextIndex].split(' ')[0].replace(/[^a-zA-Z]/g, '').length;
      playSound(piecesNow < piecesPrev ? 'capture' : 'move');

      setCurrentMoveIndex(nextIndex); updateGameSafe(history[nextIndex]);
      setSelectedSquare(null); setLegalMoves([]);
    }
  }, [currentMoveIndex, history]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') stepBackward();
      if (e.key === 'ArrowRight') stepForward();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [stepBackward, stepForward]);

  const baseRows = [8, 7, 6, 5, 4, 3, 2, 1]; const baseCols = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const rows = isFlipped ? [...baseRows].reverse() : baseRows; const cols = isFlipped ? [...baseCols].reverse() : baseCols;

  return (
    <div style={{padding: '20px', color: 'var(--text-primary)', minHeight: '100%', background: 'var(--bg-primary)' }}>
      <div style={{maxWidth: '1000px', margin: '0 auto', display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        
        {/* Left Side: Board */}
        <div style={{flex: '1 1 400px' }}>
          <h2 style={{color: '#2563eb', marginTop: 0 }}>🔬 Analysis Board</h2>
          <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', border: '4px solid rgba(163, 177, 198, 0.4)', borderRadius: '8px', overflow: 'hidden', boxShadow: 'var(--panel-shadow)' }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
              const sq = `${col}${row}`;
              const isDark = isFlipped ? (rIdx + cIdx) % 2 === 0 : (rIdx + cIdx) % 2 === 1;
              const pieceObj = game.get(sq);

              return (
                <div key={sq} 
                    onClick={() => handleSquareClick(sq)}
                    onDragOver={e => e.preventDefault()} 
                    onDrop={e => {
                        const fromSq = e.dataTransfer.getData('fromSq');
                        if(fromSq) attemptMove(fromSq, sq);
                    }}
                  style={{position: 'relative', width: '100%', height: '100%',
                    background: selectedSquare === sq ? '#2563eb' : (isDark ? darkSquareColor : lightSquareColor),
                    display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer'
                  }}>
                  
                  {/* 🔥 PRO DOTS ENGINE */}
                  {legalMoves.includes(sq) && <div style={{position: 'absolute', width: '30%', height: '30%', background: 'rgba(0,0,0,0.3)', borderRadius: '50%', zIndex: 5, pointerEvents: 'none' }} />}
                  
                  {/* Piece (Now Draggable!) */}
                  {pieceObj && <img 
                      src={currentPieceSet[pieceObj.color + pieceObj.type.toUpperCase()]} 
                      alt="piece" 
                      draggable={true}
                      onDragStart={e => e.dataTransfer.setData('fromSq', sq)}
                      onClick={(e) => { e.stopPropagation(); handleSquareClick(sq); }}
                      style={{width: '85%', height: '85%', zIndex: 10, cursor: 'grab', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))' }} 
                  />}
                  
                  {/* Coordinates */}
                  {cIdx === 0 && <span style={{position: 'absolute', top: 2, left: 3, fontSize: '11px', fontWeight: 'bold', color: isDark ? lightSquareColor : darkSquareColor, opacity: 0.5, pointerEvents: 'none' }}>{row}</span>}
                  {rIdx === 7 && <span style={{position: 'absolute', bottom: 1, right: 3, fontSize: '11px', fontWeight: 'bold', color: isDark ? lightSquareColor : darkSquareColor, opacity: 0.5, pointerEvents: 'none' }}>{col}</span>}
                </div>
              );
            }))}
          </div>

          <div style={{display: 'flex', gap: '8px', marginTop: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={stepBackward} disabled={currentMoveIndex === 0} style={{...navBtnStyle, opacity: currentMoveIndex === 0 ? 0.5 : 1}}>◀ Prev</button>
            <button onClick={stepForward} disabled={currentMoveIndex === history.length - 1} style={{...navBtnStyle, opacity: currentMoveIndex === history.length - 1 ? 0.5 : 1}}>Next ▶</button>
            <div style={{width: '2px', background: 'rgba(163, 177, 198, 0.4)', margin: '0 5px' }}></div>
            <button onClick={() => setIsFlipped(!isFlipped)} style={navBtnStyle}>🔃 Flip</button>
            <button onClick={resetBoard} style={{...navBtnStyle, background: '#ef4444'}}>🗑️ Reset</button>
          </div>
        </div>

        {/* Right Side: Controls */}
        <div style={{flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={panelStyle}>
            <h3 style={{margin: '0 0 10px 0', color: '#10b981' }}>📥 Load FEN</h3>
            <input type="text" value={fenInput} onChange={(e) => setFenInput(e.target.value)} placeholder="Paste FEN here..." style={inputStyle} />
            <button onClick={() => loadFen(fenInput)} style={{...btnStyle, background: '#10b981', color: 'black', width: '100%', marginTop: '10px'}}>Load Position</button>
          </div>

          <div style={panelStyle}>
            <h3 style={{margin: '0 0 10px 0', color: '#f59e0b' }}>📜 Load PGN</h3>
            <textarea value={pgnInput} onChange={(e) => setPgnInput(e.target.value)} placeholder="Paste PGN here..." style={{...inputStyle, height: '100px', resize: 'none'}} />
            <button onClick={() => loadPgn(pgnInput)} style={{...btnStyle, background: '#f59e0b', color: 'black', width: '100%', marginTop: '10px'}}>Load Game</button>
          </div>

          <div style={panelStyle}>
            <h3 style={{margin: '0 0 10px 0', color: '#2563eb' }}>📋 Current FEN</h3>
            <div style={{background: 'var(--bg-primary)', padding: '10px', borderRadius: '4px', wordBreak: 'break-all', fontSize: '13px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
              {fen}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(fen); toast.success("Copied!"); }} style={{...btnStyle, width: '100%', marginTop: '10px'}}>Copy FEN</button>
          </div>

        </div>
      </div>
    </div>
  );
}

const btnStyle = { border: 'none', padding: '12px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', transition: '0.2s' };
const navBtnStyle = { background: 'rgba(163, 177, 198, 0.4)', color: 'var(--text-primary)', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' };
const panelStyle = { background: 'var(--bg-primary)', padding: '25px', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)', boxShadow: 'var(--panel-shadow)' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid rgba(163, 177, 198, 0.4)', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box', fontFamily: 'monospace' };
